/* eslint-disable no-use-before-define */
import { debounce, ScrollService } from '@workbench/core';
import { onBeforeUnmount } from 'vue';
import { Logger } from './logger.util';
import { waitForElementToBeAdded } from './query-element.util';

export type ScrollOptions = ScrollIntoViewOptions & {
  container?: Document | HTMLElement | ShadowRoot;
  offset?: number;
};

/**
 * Calculates the scroll position for the element with the given element id. It
 * then calls the scroll to position function to do the scroll.
 *
 * A negative offset would scroll to a position above the given target element,
 * while a positive offset scrolls into the given target element.
 */
export function scrollToElement(el: string | HTMLElement, options: ScrollOptions): void {
  const { container = document, offset = 0, behavior, block, inline } = options;
  const element = (el && typeof el === 'string' ? container?.querySelector(el) : el) as HTMLElement;

  if (!element) {
    Logger.warn(`Could not find element. Aborting scroll.`, el);

    return;
  }

  if (block || inline) {
    element.scrollIntoView({ behavior, block, inline });

    if (offset === 0) {
      return;
    }
  }

  const viewportOffset = element.getBoundingClientRect().top;
  const scrollPosition =
    (container as Document).documentElement?.scrollTop ||
    (container as Document).body?.scrollTop ||
    (container as HTMLElement).scrollTop ||
    0;

  scrollToPosition(scrollPosition + viewportOffset + offset);
}

/**
 * Smoothly scrolls to the given position while keeping the boundaries of the
 * document. The default scroll speed is 8. Increase the value to make it
 * scroll faster.
 */
export function scrollToPosition(targetPosition: number): void {
  const documentHeight = (document as any).height || document.body.clientHeight;
  const viewPortHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

  // check if offset is still in the document boundary
  if (targetPosition < 0) {
    Logger.debug('Scroll target is out of reach. Resetting scroll target.');
    targetPosition = 0;
  } else if (targetPosition > documentHeight - viewPortHeight) {
    targetPosition = documentHeight - viewPortHeight;
  }

  // make the scroll cancelable if the user uses the scroll wheel
  window.addEventListener('wheel', abortScroll, { passive: true });

  let stopScroll = false;
  let request: number;

  scroll();

  function scroll() {
    const scrollSpeed = 8;
    const scrollValue = document.documentElement.scrollTop || document.body.scrollTop;

    if (stopScroll) {
      return;
    }

    const deltaToTarget = Math.abs(Math.round(scrollValue - Math.abs(targetPosition)));

    if (deltaToTarget > 1) {
      // for IE11 we have to explicitly set the scrollToValue to 0 if the scroll
      // position is smaller than the scroll speed value. Otherwise, it will never
      // get smaller than the scroll speed leading to an infinite scroll loop
      const scrollToValue =
        deltaToTarget <= scrollSpeed ? targetPosition : scrollValue + (targetPosition - scrollValue) / scrollSpeed;

      request = window.requestAnimationFrame(scroll);
      window.scrollTo(0, scrollToValue);
    } else {
      stopScroll = true;
      window.cancelAnimationFrame(request);
    }
  }

  function abortScroll() {
    stopScroll = true;
    window.removeEventListener('wheel', abortScroll);
  }
}

export function useScrollBack() {
  const scrollBack = async (anchor): Promise<void> => {
    const el = await waitForElementToBeAdded(`[data-anchor="${anchor}"]`);
    const offsetTop = el?.parentElement?.offsetTop;

    if (el && offsetTop) {
      window.scrollTo({
        top: offsetTop - 86,
        left: 0,
        behavior: 'auto',
      });
    }
  };

  const register = (anchor: string) => {
    window.addEventListener('popstate', () => scrollBack(anchor), { once: true });
  };

  return { register, scrollBack };
}

/**
 * optimized way to register scroll event to the page
 */
const scrollService = new ScrollService();

export const onScroll = (fn: () => void, timeout = 0) => {
  const callback = timeout ? debounce(fn, timeout) : fn;

  scrollService.register(callback);

  onBeforeUnmount(() => {
    scrollService.unregister(callback);
  });
};
